import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type {
  ConversationListContactStateName,
  ConversationListSortName,
  ConversationListStatusName,
  PlatformTypeName,
} from "@multi-chat/shared";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { cn } from "../../../lib/utils.js";
import type { MobileFilterSortDraft } from "../conversation-list-filters.js";

const platformChoices: Array<{ value: PlatformTypeName | undefined; labelKey: string }> = [
  { value: undefined, labelKey: "inbox.filters.platform.all" },
  { value: "WHATSAPP", labelKey: "channel.whatsapp" },
  { value: "INSTAGRAM", labelKey: "channel.instagram" },
  { value: "FACEBOOK", labelKey: "channel.messenger" },
];

const readChoices: Array<{ value: boolean; labelKey: string }> = [
  { value: false, labelKey: "inbox.filters.read.all" },
  { value: true, labelKey: "inbox.filters.unreadOnly" },
];

const contactChoices: Array<{
  value: ConversationListContactStateName | undefined;
  labelKey: string;
}> = [
  { value: undefined, labelKey: "inbox.filters.contact.all" },
  { value: "SAVED", labelKey: "inbox.filters.contact.saved" },
  { value: "UNSAVED", labelKey: "inbox.filters.contact.unsaved" },
];

const statusChoices: Array<{ value: ConversationListStatusName; labelKey: string }> = [
  { value: "ALL", labelKey: "inbox.filters.status.all" },
  { value: "OPEN", labelKey: "inbox.filters.status.open" },
  { value: "CLOSED", labelKey: "inbox.filters.status.closed" },
];

const sortChoices: Array<{ value: ConversationListSortName; labelKey: string }> = [
  { value: "activity_desc", labelKey: "inbox.sort.newestFirst" },
  { value: "activity_asc", labelKey: "inbox.sort.oldestFirst" },
];

/** Channel, read state, contact, and sort as pill radio groups. Shared by the popover and mobile sheet. */
export function ConversationFilterSections({
  value,
  onChange,
}: {
  value: MobileFilterSortDraft;
  onChange: (next: MobileFilterSortDraft) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const sortLabel = t(
    sortChoices.find((choice) => choice.value === value.sort)?.labelKey ?? "inbox.sort.newestFirst",
  );

  return (
    <div className="divide-y divide-inbox-line-soft">
      <FilterSection heading={t("inbox.mobile.channelHeading")}>
        {platformChoices.map((choice) => (
          <FilterOption
            key={choice.labelKey}
            checked={value.platform === choice.value}
            onSelect={() => onChange({ ...value, platform: choice.value })}
          >
            {choice.value ? (
              <ChannelIdentityBadge platformType={choice.value} size="filter" />
            ) : null}
            {t(choice.labelKey)}
          </FilterOption>
        ))}
      </FilterSection>
      <FilterSection heading={t("inbox.filters.unreadHeading")}>
        {readChoices.map((choice) => (
          <FilterOption
            key={choice.labelKey}
            checked={value.unread === choice.value}
            onSelect={() => onChange({ ...value, unread: choice.value })}
          >
            {t(choice.labelKey)}
          </FilterOption>
        ))}
      </FilterSection>
      <FilterSection heading={t("inbox.filters.contactHeading")}>
        {contactChoices.map((choice) => (
          <FilterOption
            key={choice.labelKey}
            checked={value.contactState === choice.value}
            onSelect={() => onChange({ ...value, contactState: choice.value })}
          >
            {t(choice.labelKey)}
          </FilterOption>
        ))}
      </FilterSection>
      <FilterSection heading={t("inbox.filters.statusHeading")}>
        {statusChoices.map((choice) => (
          <FilterOption
            key={choice.value}
            checked={value.status === choice.value}
            onSelect={() => onChange({ ...value, status: choice.value })}
          >
            {t(choice.labelKey)}
          </FilterOption>
        ))}
      </FilterSection>
      <FilterSection heading={t("inbox.mobile.sortHeading")} current={sortLabel}>
        {sortChoices.map((choice) => (
          <FilterOption
            key={choice.value}
            checked={value.sort === choice.value}
            onSelect={() => onChange({ ...value, sort: choice.value })}
          >
            {t(choice.labelKey)}
          </FilterOption>
        ))}
      </FilterSection>
    </div>
  );
}

function FilterSection({
  heading,
  current,
  children,
}: {
  heading: string;
  current?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="px-3.5 pt-3 pb-1">
      <div className="mb-2 flex items-center gap-2 text-[0.75rem] font-bold text-inbox-muted">
        <span>{heading}</span>
        {current ? (
          <span className="ms-auto font-semibold text-primary">{current}</span>
        ) : null}
      </div>
      <div role="radiogroup" aria-label={heading} className="mb-2 flex flex-wrap gap-1.5">
        {children}
      </div>
    </div>
  );
}

function FilterOption({
  checked,
  onSelect,
  children,
}: {
  checked: boolean;
  onSelect: () => void;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={cn(
        "inline-flex h-[1.875rem] items-center gap-1.5 rounded-full border px-[0.6875rem] text-[0.78125rem] transition-[background-color,border-color] duration-150",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
        checked
          ? "border-[#9fd6c3] bg-brand-mint font-[650] text-[#126c56]"
          : "border-[#dbe5e2] bg-surface text-[#30443f] hover:border-[#b9d4cb]",
      )}
    >
      {children}
    </button>
  );
}
